import { FormulaSyntaxError, parseFormula, references } from './formula';
import { TemplateDoc } from './template';

/**
 * Checking a template written by hand or by an AI assistant from a printed report.
 *
 * The JSON that comes in need not be tidy: fences around it, numbers written as text and missing
 * ids are fixed, and each fix is reported so nothing changes without being said. What cannot be
 * fixed is reported as an error with where it is, in words a person can act on.
 */

type Json = Record<string, unknown>;
const isObj = (v: unknown): v is Json => typeof v === 'object' && v !== null && !Array.isArray(v);

export interface TemplateCheck {
  /** True when `doc` can be saved. */
  ok: boolean;
  doc: TemplateDoc | null;
  errors: string[];
  /** Worth a look, but the template can still be saved. */
  warnings: string[];
  /** Things that were put right on the way in. */
  fixes: string[];
  stats: {
    tests: number;
    sections: number;
    notes: number;
    narratives: number;
    calculated: number;
    withRanges: number;
  };
}

/** Read pasted text as JSON, tolerating a code fence or a leading note around it. */
export function readTemplateJson(text: string): { value: unknown } | { error: string } {
  let s = text.replace(/^﻿/, '').trim();
  if (!s) return { error: 'Paste the template JSON here.' };
  const fence = /```(?:json|JSON)?\s*([\s\S]*?)```/.exec(s);
  if (fence?.[1]) s = fence[1].trim();
  if (!s.startsWith('{') && !s.startsWith('[')) {
    const a = s.indexOf('{');
    const b = s.lastIndexOf('}');
    if (a >= 0 && b > a) s = s.slice(a, b + 1);
  }
  try {
    return { value: JSON.parse(s) };
  } catch (e) {
    const m = e instanceof Error ? e.message : 'not valid JSON';
    return { error: `This is not valid JSON: ${m}` };
  }
}

const NUMERIC_KEYS = new Set(['min', 'max', 'ageMinDays', 'ageMaxDays', 'low', 'high']);

function coerceNumbers(v: unknown, fixes: string[], where: string): unknown {
  if (Array.isArray(v)) return v.map((x, i) => coerceNumbers(x, fixes, `${where}[${i}]`));
  if (!isObj(v)) return v;
  const out: Json = {};
  for (const [k, x] of Object.entries(v)) {
    if (
      NUMERIC_KEYS.has(k) &&
      typeof x === 'string' &&
      x.trim() !== '' &&
      !Number.isNaN(Number(x))
    ) {
      out[k] = Number(x);
      fixes.push(`${where}.${k}: "${x}" is written as a number`);
    } else if (k === 'bands' || k === 'ranges' || k === 'critical') {
      out[k] = coerceNumbers(x, fixes, `${where}.${k}`);
    } else {
      out[k] = x;
    }
  }
  return out;
}

const DEFAULT_COLUMNS = [
  { id: 'c1', role: 'investigation', label: 'Test Name' },
  { id: 'c2', role: 'result', label: 'Result' },
  { id: 'c3', role: 'unit', label: 'Unit' },
  { id: 'c4', role: 'ref_range', label: 'Bio. Ref. Interval' },
];

/** A test code the way the report engine wants it: capitals, digits and _, starting with a letter. */
export function tidyCode(raw: string): string {
  let c = raw
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!c) c = 'T';
  if (!/^[A-Z]/.test(c)) c = `T${c}`;
  return c;
}

/** Put loose JSON into the shape the template schema expects, noting each change. */
export function normalizeTemplateJson(input: unknown): { value: unknown; fixes: string[] } {
  const fixes: string[] = [];
  let idsAdded = 0;
  let doc = input;
  // `{ "template": { ... } }` is accepted as well as the template itself.
  if (isObj(doc) && isObj(doc.template) && !('rows' in doc)) doc = doc.template;
  if (!isObj(doc)) return { value: doc, fixes };
  const out: Json = { ...doc };

  if (!Array.isArray(out.columns) || out.columns.length === 0) {
    out.columns = DEFAULT_COLUMNS;
    fixes.push(
      'No columns were given, so the usual four are used: Test Name, Result, Unit, Reference',
    );
  } else {
    const used = new Set<string>();
    out.columns = out.columns.map((c, i) => {
      if (!isObj(c)) return c;
      let id = typeof c.id === 'string' && c.id ? c.id : `c${i + 1}`;
      if (used.has(id)) id = `${id}_${i + 1}`;
      used.add(id);
      if (c.id !== id) idsAdded++;
      return { ...c, id };
    });
  }

  if (Array.isArray(out.rows)) {
    const used = new Set<string>();
    const codes = new Set<string>();
    out.rows = out.rows.map((raw, i) => {
      if (!isObj(raw)) return raw;
      let r: Json = { ...raw };
      const n = `Row ${i + 1}`;
      if (r.kind === undefined) {
        r.kind =
          'code' in r || 'ranges' in r || 'unit' in r ? 'test' : 'text' in r ? 'note' : 'section';
        fixes.push(`${n}: treated as a ${r.kind === 'test' ? 'test' : r.kind}`);
      }
      let id = typeof r.id === 'string' && r.id ? r.id : `r${i + 1}`;
      if (used.has(id)) id = `${id}_${i + 1}`;
      used.add(id);
      if (r.id !== id) idsAdded++;
      r.id = id;
      if (r.kind === 'test') {
        if (typeof r.code === 'string') {
          const tidy = tidyCode(r.code);
          if (tidy !== r.code) fixes.push(`${n}: test code "${r.code}" is written as ${tidy}`);
          r.code = tidy;
        } else if (typeof r.label === 'string') {
          r.code = tidyCode(r.label).slice(0, 12);
          fixes.push(`${n}: no test code was given, so ${r.code} is used`);
        }
        // Two tests with the same code: keep the first and number the others.
        if (typeof r.code === 'string') {
          let c = r.code;
          let k = 2;
          while (codes.has(c)) c = `${r.code}_${k++}`;
          if (c !== r.code)
            fixes.push(`${n}: test code ${r.code} was already used, so ${c} is used`);
          codes.add(c);
          r.code = c;
        }
        if (typeof r.decimals === 'string') r.decimals = Number(r.decimals);
        if (Array.isArray(r.options) && r.options.length > 0 && r.valueType === undefined) {
          r.valueType = 'option';
          fixes.push(`${n}: it has options, so it is a pick-from-a-list test`);
        }
        if (r.ranges !== undefined) {
          r = {
            ...r,
            ranges: (coerceNumbers(r.ranges, fixes, `${n}.ranges`) as unknown[]).map((x) => {
              if (!isObj(x) || x.type !== undefined) return x;
              const type =
                'min' in x && 'max' in x
                  ? 'between'
                  : 'max' in x
                    ? 'lte'
                    : 'min' in x
                      ? 'gte'
                      : 'expected' in x
                        ? 'qualitative'
                        : 'bands' in x
                          ? 'bands'
                          : undefined;
              if (!type) return x;
              fixes.push(`${n}: a reference range with no type is read as "${type}"`);
              return { ...x, type };
            }),
          };
        }
        if (r.critical !== undefined)
          r.critical = coerceNumbers(r.critical, fixes, `${n}.critical`);
      }
      return r;
    });
  }
  if (
    typeof out.price === 'string' &&
    out.price.trim() !== '' &&
    !Number.isNaN(Number(out.price))
  ) {
    out.price = Number(out.price);
    fixes.push(`price: "${out.price}" is written as a number`);
  }
  if (idsAdded > 0) {
    fixes.unshift(`${idsAdded} column and row ids were added`);
  }
  return { value: out, fixes };
}

function where(path: PropertyKey[], doc: unknown): string {
  const parts: string[] = [];
  let cur: unknown = doc;
  for (const p of path) {
    if (typeof p === 'number') {
      const label =
        isObj(cur) || Array.isArray(cur) ? ((cur as unknown[])[p] as Json | undefined) : undefined;
      const name =
        label && isObj(label) ? (label.label ?? label.code ?? label.text ?? label.role) : undefined;
      parts.push(`#${p + 1}${typeof name === 'string' && name ? ` “${name.slice(0, 30)}”` : ''}`);
      cur = (cur as unknown[])?.[p];
    } else {
      parts.push(String(p));
      cur = isObj(cur) ? cur[String(p)] : undefined;
    }
  }
  return parts.join(' › ');
}

/** Everything wrong with, and worth knowing about, a template written as JSON. */
export function checkTemplate(input: unknown): TemplateCheck {
  const { value, fixes } = normalizeTemplateJson(input);
  const errors: string[] = [];
  const warnings: string[] = [];
  const stats = { tests: 0, sections: 0, notes: 0, narratives: 0, calculated: 0, withRanges: 0 };

  const parsed = TemplateDoc.safeParse(value);
  if (!parsed.success) {
    for (const issue of parsed.error.issues.slice(0, 12)) {
      const at = where(issue.path, value);
      errors.push(at ? `${at}: ${issue.message}` : issue.message);
    }
    if (parsed.error.issues.length > 12) {
      errors.push(`…and ${parsed.error.issues.length - 12} more`);
    }
    return { ok: false, doc: null, errors, warnings, fixes, stats };
  }
  const doc = parsed.data;

  const codes = new Set<string>();
  for (const r of doc.rows) if (r.kind === 'test') codes.add(r.code);
  for (const r of doc.rows) {
    if (r.kind === 'section') stats.sections++;
    else if (r.kind === 'note') stats.notes++;
    else if (r.kind === 'narrative') stats.narratives++;
    else {
      stats.tests++;
      if (r.formula) stats.calculated++;
      if (r.ranges.length > 0) stats.withRanges++;
      const name = `“${r.label}”`;
      if (r.formula) {
        try {
          const refs = references(parseFormula(r.formula));
          for (const c of refs) {
            if (!codes.has(c))
              errors.push(`${name}: the formula uses ${c}, which is not a test code here`);
            if (c === r.code) errors.push(`${name}: the formula uses its own code`);
          }
        } catch (e) {
          errors.push(
            `${name}: the formula "${r.formula}" cannot be read${e instanceof FormulaSyntaxError ? ` (${e.message})` : ''}`,
          );
        }
      }
      if (r.valueType === 'option' && !(r.options && r.options.length > 0)) {
        errors.push(`${name}: it is a pick-from-a-list test but has no options`);
      }
      for (const [i, g] of r.ranges.entries()) {
        if (g.type === 'between' && g.min > g.max) {
          errors.push(`${name}: reference range ${i + 1} has its minimum above its maximum`);
        }
      }
      if (r.allowOther && r.valueType !== 'option') {
        warnings.push(`${name} allows typing, which only applies to pick-from-a-list tests`);
      }
      if (r.valueType === 'numeric' && !r.formula && r.ranges.length === 0) {
        warnings.push(`${name} has no reference range, so it will never be flagged`);
      }
      if (r.valueType === 'numeric' && r.ranges.some((g) => g.type === 'qualitative')) {
        warnings.push(`${name} is a number but has a words-based range`);
      }
    }
  }
  if (stats.tests === 0 && stats.narratives === 0) {
    errors.push('The template has no tests. Add rows with "kind": "test".');
  }
  if (!doc.department) warnings.push('No department is set, so it will not be grouped on reports');
  if (!doc.sampleType) warnings.push('No sample type is set');
  if (doc.aliases.length === 0)
    warnings.push('No other names (aliases), so it is harder to find by search');

  // A cycle in the formulas (A needs B, B needs A) can never be worked out.
  const deps = new Map<string, string[]>();
  for (const r of doc.rows) {
    if (r.kind !== 'test' || !r.formula) continue;
    try {
      deps.set(r.code, [...references(parseFormula(r.formula))]);
    } catch {
      // already reported above
    }
  }
  const state = new Map<string, 1 | 2>();
  const cyc = (c: string): boolean => {
    if (state.get(c) === 2) return false;
    if (state.get(c) === 1) return true;
    state.set(c, 1);
    for (const d of deps.get(c) ?? []) if (cyc(d)) return true;
    state.set(c, 2);
    return false;
  };
  for (const c of deps.keys()) {
    if (cyc(c)) {
      errors.push('The formulas depend on each other in a circle, so they cannot be worked out');
      break;
    }
  }

  return { ok: errors.length === 0, doc, errors, warnings, fixes, stats };
}

/** What a template is searched by: its name, other names, department and tags. */
export function templateSearchText(doc: TemplateDoc): string {
  return [doc.name, ...doc.aliases, doc.department ?? '', ...doc.tags].join(' ').toLowerCase();
}
